<script setup lang="ts">
import { api } from '~/api/apis'
import type { ProductMainListData } from '~/api/schemas/product-main-list-data'
import type { ProductSubListData } from '~/api/schemas/product-sub-list-data'
import { useLocale } from '~/composables/use-locale'
import { TrueFalse } from '~/enums/true-false'

const { t } = useI18n()
const { getLangText } = useLocale()

useHead({
  title: t('meta_home_title'),
  meta: [
    {
      name: 'title',
      content: t('meta_home_title')
    },
    {
      name: 'keywords',
      content: t('meta_home_keywords')
    },
    {
      name: 'description',
      content: t('meta_home_description')
    },
    {
      property: 'og:title',
      content: t('meta_home_title')
    },
    {
      name: 'og:keywords',
      content: t('meta_home_keywords')
    },
    {
      property: 'og:description',
      content: t('meta_home_description')
    },
    { property: 'og:url', content: 'https://www.tpisoftware.com/portal/' },
    {
      property: 'og:image',
      content: 'https://www.tpisoftware.com/portal/images/og-image.png'
    },
    { property: 'og:type', content: 'website' }
  ]
})

definePageMeta({
  middleware: useMiddleware().appMiddleware
})

const productMainList = useAsyncData(
  async () => {
    return useAppApi(
      api.getProductMainList({
        isEnable: [TrueFalse.TRUE]
      })
    ).call()
  },
  {
    transform: (result) => {
      return result.success
        ? (
            result.response.data?.filter(
              (data) => data.pmCode !== 'tpSalesman'
            ) ?? []
          ).sort((a, b) => Number(a.sort) - Number(b.sort))
        : []
    }
  }
)

const productSubList = useAsyncData(
  () => {
    return useAppApi(
      api.getProductSubList({
        sort: 'sort',
        order: 'asc',
        isEnable: [TrueFalse.TRUE]
      })
    ).call()
  },
  {
    transform: (result) => {
      return result.success
        ? result.response.data?.sort((a, b) => Number(a.sort) - Number(b.sort))
        : []
    }
  }
)

const { accountRole, user } = storeToRefs(useUserStore())

const router = useRouter()
const localePath = useLocalePath()
const { getUploadFileUrl } = useUrl()

const getFreeDownload = computed(
  () => 'https://www.tpisoftware.com/products/digimars#apply'
)

function getProductMainSubList(id: number): ProductSubListData[] {
  return (
    productSubList.data.value?.filter((item) => item.pmID === id).slice(0, 6) ??
    []
  )
}

function getProductSubName(data?: ProductSubListData): string {
  return data ? getLangText(data, 'name') : ''
}

async function onClickLabel(pm: ProductMainListData, ps: ProductSubListData) {
  const getProductMainResult = await useAppApi(
    api.getProductMain(pm.pmID)
  ).call()

  if (!getProductMainResult.success || !getProductMainResult.response.data) {
    return
  }

  const userRoleList = user.value?.role || []
  const canAccess = getProductMainResult.response.data.role.some((role) =>
    userRoleList.includes(role)
  )

  if (canAccess) {
    router.push(localePath({ path: `/product-navs/${pm.pmID}/${ps.psID}` }))
  } else {
    router.push(localePath({ path: `/product-list/${pm.pmID}` }))
  }
}

function onClickSubmitLabel(pm: ProductMainListData) {
  router.push(localePath({ path: '/submit', query: { p: `${pm.pmID}` } }))
}

function isUserProductMainRole(roleCode: string) {
  return accountRole.value.some((role) => roleCode.includes(role))
}
</script>

<template>
  <div>
    <section class="about-bg">
      <app-search-bar class="about-search" icon-class="icon-search" />
    </section>

    <section class="portal container-fluid">
      <div class="portal-bg01"></div>
      <div class="portal-bg02"></div>
      <div class="portal-group">
        <div
          v-for="productMain in productMainList.data.value"
          :key="productMain.pmID"
          class="portal-rhex"
        >
          <div class="portal-info">
            <div class="portal-logo d-block">
              <img
                alt=""
                :src="
                  productMain.productLogo
                    ? getUploadFileUrl(productMain.productLogo, 'productMain')
                    : ''
                "
              />
              <div class="text-center">
                <p>
                  {{ getLangText(productMain, 'description') }}
                </p>
              </div>
            </div>
            <div class="portal-bottom">
              <div class="portal-title-group">
                <div class="portal-title">
                  {{ getLangText(productMain, 'name') }}
                </div>
                <div
                  v-if="isUserProductMainRole(productMain.roleCode)"
                  class="portal-submit"
                >
                  <a
                    class="cursor-pointer"
                    @click="onClickSubmitLabel(productMain)"
                  >
                    {{ t('index_submit') }}
                  </a>
                </div>
              </div>
              <div class="portal-link">
                <span
                  v-for="(productSub, index) in getProductMainSubList(
                    productMain.pmID
                  )"
                  :key="index"
                >
                  <a
                    v-if="getProductMainSubList(productMain.pmID)[index]"
                    class="cursor-pointer"
                    @click.prevent="
                      onClickLabel(
                        productMain,
                        getProductMainSubList(productMain.pmID)[index]
                      )
                    "
                  >
                    {{
                      getProductSubName(
                        getProductMainSubList(productMain.pmID)[index]
                      )
                    }}
                  </a>
                </span>
                <span v-if="productMain.pmCode === 'Gudab'">
                  <a
                    class="cursor-pointer"
                    target="_blank"
                    :href="getFreeDownload"
                  >
                    {{ $t('index_free_download') }}
                  </a>
                </span>
              </div>
              <nuxt-link
                class="portal-more"
                :to="localePath({ path: `/product-list/${productMain.pmID}` })"
              >
                {{ $t('index_more') }}
                <img alt="" src="~/assets/app/images/arrow.svg" />
              </nuxt-link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.portal-title-group {
  display: flex;

  .portal-title {
    width: 50% !important;
  }
  .portal-submit {
    width: 50%;
    padding-left: 0.5rem;
    margin-bottom: 0.5rem;
    line-height: 1;
    border-left: 4px solid #000;
  }
}

body.app .portal .portal-group .portal-rhex .portal-info .portal-logo {
  width: 400px;
  text-align: center;

  img {
    width: 170px;
  }
}
</style>
